import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import Navbar from '../components/Navbar';
import PriceChart, { parseChartJson } from '../components/PriceChart';

const SITE_URL = 'https://www.workbees.space';

interface NewsPost {
  slug: string;
  title: string;
  date: string;
  description?: string;
  tags?: string;
  pipeline?: string;
  content: string;
}

/** 动态更新 meta 标签（SEO + 分享卡片） */
function updateMetaTag(name: string, content: string) {
  let meta = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`) as HTMLMetaElement;
  if (!meta) {
    meta = document.createElement('meta');
    if (name.startsWith('og:')) {
      meta.setAttribute('property', name);
    } else {
      meta.setAttribute('name', name);
    }
    document.head.appendChild(meta);
  }
  meta.content = content;
}

// 与产品文档页一致的 Markdown 渲染风格
const markdownComponents = {
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="text-3xl font-bold mt-8 mb-6 text-slate-800">{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="text-2xl font-bold mt-10 mb-4 text-slate-800 flex items-center gap-2">
      <span className="w-1 h-6 bg-indigo-500 rounded-full"></span>
      {children}
    </h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-800">{children}</h3>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="mb-4 text-slate-600 leading-relaxed">{children}</p>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="list-disc list-inside my-4 space-y-2 text-slate-700">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="list-decimal list-inside my-4 space-y-2 text-slate-700">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="text-slate-600">{children}</li>
  ),
  // 外层 pre 剥壳：code 组件自己负责整体样式（chart 代码块在这里替换为图表）
  pre: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
  code: ({ inline, className, children }: { inline?: boolean; className?: string; children?: React.ReactNode }) => {
    if (inline) {
      return (
        <code className="px-1.5 py-0.5 mx-0.5 rounded bg-slate-100 text-indigo-600 font-mono text-sm border border-indigo-100">
          {children}
        </code>
      );
    }

    const lang = /language-([\w-]+)/.exec(className || '')?.[1];

    // 内部数据块：不渲染
    if (lang === 'pricing-data') return null;

    // 走势图：chart 代码块 → SVG 折线图（解析失败降级为普通代码块）
    if (lang === 'chart') {
      const data = parseChartJson(String(children));
      if (data) return <PriceChart data={data} />;
    }

    return (
      <pre className="block px-4 py-3 my-4 rounded-lg bg-slate-900 text-slate-100 font-mono text-sm overflow-auto border border-slate-700">
        <code>{children}</code>
      </pre>
    );
  },
  blockquote: ({ children }: { children?: React.ReactNode }) => (
    <blockquote className="border-l-4 border-indigo-300 bg-indigo-50 pl-4 pr-4 py-3 my-6 italic text-slate-600 rounded-r">
      {children}
    </blockquote>
  ),
  table: ({ children }: { children?: React.ReactNode }) => (
    <table className="w-full my-6 border-collapse border border-slate-200 rounded-lg overflow-hidden">
      {children}
    </table>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="border-b-2 border-slate-300 px-4 py-3 text-left font-semibold text-slate-800 bg-slate-50">
      {children}
    </th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td className="border-b border-slate-200 px-4 py-3 text-slate-600">
      {children}
    </td>
  ),
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a
      href={href}
      className="text-indigo-600 underline underline-offset-4 hover:text-indigo-700 transition-colors"
      target={href?.startsWith('http') ? '_blank' : undefined}
      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
    >
      {children}
    </a>
  ),
  hr: () => <hr className="my-8 border-t-2 border-slate-200" />,
  img: ({ src, alt }: { src?: string; alt?: string }) => (
    <img src={src} alt={alt || ''} className="max-w-full rounded-xl my-6 border border-slate-100" />
  ),
};

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<NewsPost | null>(null);
  const [related, setRelated] = useState<NewsPost[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'missing'>('loading');

  // 相关文章：拉全量列表，按共享 tag 数取前 3（不足用最新文章补齐）
  useEffect(() => {
    fetch('/api/news')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((list: NewsPost[]) => {
        if (!Array.isArray(list) || !slug) return;
        const cur = list.find((p) => p.slug === slug);
        if (!cur?.tags) {
          setRelated(list.filter((p) => p.slug !== slug).slice(0, 3));
          return;
        }
        const curTags = new Set(cur.tags.split(',').map((t) => t.trim()).filter(Boolean));
        const scored = list
          .filter((p) => p.slug !== slug)
          .map((p) => ({
            post: p,
            score: (p.tags || '').split(',').filter((t) => curTags.has(t.trim())).length,
          }))
          .sort((a, b) => b.score - a.score);
        setRelated(scored.slice(0, 3).map((s) => s.post));
      })
      .catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (!slug) {
      setState('missing');
      return;
    }
    let cancelled = false;
    setState('loading');
    fetch(`/api/news/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data) => {
        if (cancelled) return;
        setPost(data);
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('missing');
      });
    window.scrollTo(0, 0);
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} | Teegal AI NEWS`;
      const desc = post.description || post.content.slice(0, 150).replace(/[#*`>\-\n]/g, ' ').trim();
      updateMetaTag('description', desc);
      updateMetaTag('og:title', post.title);
      updateMetaTag('og:description', desc);
      updateMetaTag('og:type', 'article');
      updateMetaTag('og:url', `${SITE_URL}/blog/${post.slug}`);
      document.querySelector('link[rel="canonical"]')?.setAttribute('href', `${SITE_URL}/blog/${post.slug}`);
    }
  }, [post]);

  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 pt-40 pb-24 text-center text-slate-400">
          <Loader2 size={24} className="animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 pt-40 pb-24 text-center">
          <h1 className="text-2xl font-bold text-slate-800 mb-4">文章不存在</h1>
          <Link to="/blog" className="text-indigo-600 hover:underline">← 返回 AI NEWS</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="pt-14">
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-24">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-600 mb-8 transition-colors">
            <ArrowLeft size={15} />
            返回 AI NEWS
          </Link>

          <header className="mb-10">
            <div className="flex items-center gap-2 mb-3">
              {post.pipeline === 'compute-pricing' ? (
                <span className="rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[11px] font-medium text-amber-700">算力价格</span>
              ) : post.pipeline ? (
                <span className="rounded-full bg-indigo-50 border border-indigo-100 px-2 py-0.5 text-[11px] font-medium text-indigo-600">深度报道</span>
              ) : null}
              <div className="flex items-center gap-1.5 text-sm text-slate-400">
                <Calendar size={14} />
                <span>{post.date}</span>
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4 leading-tight">
              {post.title}
            </h1>
            {post.tags && (
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {post.tags.split(',').filter(Boolean).map((tag) => (
                  <span key={tag} className="text-[11px] text-slate-400">
                    #{tag.trim()}
                  </span>
                ))}
              </div>
            )}
          </header>

          <div className="bg-white rounded-2xl border border-slate-100 px-6 sm:px-10 py-8">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={markdownComponents as any}
            >
              {post.content}
            </ReactMarkdown>
          </div>

          {/* 相关文章（内链，SEO 友好） */}
          {related.length > 0 && (
            <section className="mt-10">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">相关文章</h3>
              <div className="grid gap-3 sm:grid-cols-3">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/blog/${r.slug}`}
                    className="block p-4 rounded-xl bg-white border border-slate-100 hover:border-indigo-200 hover:shadow-sm transition-all"
                  >
                    <span className="text-[11px] text-slate-400">{r.date}</span>
                    <h4 className="text-sm font-medium text-slate-700 leading-snug mt-1 line-clamp-2">
                      {r.title}
                    </h4>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
    </div>
  );
};

export default BlogPost;
